import { ActionIcon, Badge, Box, Flex, Group, Progress, Stack, Text, Tooltip } from '@mantine/core';
import { IconCopyPlus } from '@tabler/icons-react';
import { Link } from 'react-router';
import type { ClientRequestSummaryDto } from '@mexp/shared';
import { expectedLabel, fullDate, stageLabel, STAGE_COLOR } from './stage';

/** The soonest expected date first, then dossiers still waiting for Makin's date, by last activity. */
export function byUrgency(a: ClientRequestSummaryDto, b: ClientRequestSummaryDto): number {
  if (a.requiredBy && b.requiredBy) return a.requiredBy < b.requiredBy ? -1 : a.requiredBy > b.requiredBy ? 1 : 0;
  if (a.requiredBy) return -1;
  if (b.requiredBy) return 1;
  return a.updatedAt < b.updatedAt ? 1 : -1;
}

/** Most recent first, by when the request was completed or cancelled (or last touched). */
export function byRecency(a: ClientRequestSummaryDto, b: ClientRequestSummaryDto): number {
  const at = (r: ClientRequestSummaryDto) => r.completedAt ?? r.cancelledAt ?? r.updatedAt;
  return at(b).localeCompare(at(a));
}

/** Open dossiers most urgent first, then the rest most recent first. */
export function openFirst(items: ClientRequestSummaryDto[]): ClientRequestSummaryDto[] {
  const closed = (r: ClientRequestSummaryDto) => r.stage === 'completed' || r.stage === 'cancelled';
  return [...items.filter((r) => !closed(r)).sort(byUrgency), ...items.filter(closed).sort(byRecency)];
}

/**
 * Starts a new requisition for the same product (a product can have several: other destinations, other needs).
 * The form fills in the product's details from `from`.
 */
export function RepeatProductAction({ from, productName, mt }: { from: string; productName: string; mt?: number }) {
  return (
    <Tooltip label="New requisition for this product" openDelay={300}>
      <ActionIcon
        component={Link}
        to={`/requests/new?from=${from}`}
        variant="subtle"
        color="gray"
        size="lg"
        mt={mt}
        aria-label={`New requisition for ${productName}`}
        style={{ flex: 'none' }}
      >
        <IconCopyPlus size={18} stroke={1.7} />
      </ActionIcon>
    </Tooltip>
  );
}

/**
 * The line under a dossier's name: its pack size, and its generic name when the name ("Brand name / Generic name")
 * does not already say it; null when there is nothing to add.
 */
export function productDetails(r: Pick<ClientRequestSummaryDto, 'productName' | 'genericName' | 'packSize'>): string | null {
  const parts = [r.genericName && !r.productName.includes(r.genericName) ? `Generic: ${r.genericName}` : null, r.packSize ? `Pack: ${r.packSize}` : null];
  return parts.filter(Boolean).join(' · ') || null;
}

/** One dossier: the row opens it; the icon beside it starts another requisition for the same product. */
export function RequestRow({ r, active = false, onHover }: { r: ClientRequestSummaryDto; active?: boolean; onHover?: (id: string | null) => void }) {
  const open = r.stage !== 'completed' && r.stage !== 'cancelled';
  const pct = r.progress.total ? Math.round((r.progress.ready / r.progress.total) * 100) : 0;
  const version =
    r.releasedVersions > 0
      ? open
        ? 'Dossier available · query open'
        : r.stage === 'completed' && r.releasedVersions > 1
          ? `Version ${r.releasedVersions}`
          : null
      : null;
  return (
    <Group gap={4} wrap="nowrap" align="flex-start">
      <Box
        component={Link}
        to={`/requests/${r.id}`}
        className="portal-row"
        data-active={active || undefined}
        style={{ flex: 1, minWidth: 0 }}
        onMouseEnter={() => onHover?.(r.id)}
        onMouseLeave={() => onHover?.(null)}
        onFocus={() => onHover?.(r.id)}
        onBlur={() => onHover?.(null)}
      >
        <Flex direction={{ base: 'column', xs: 'row' }} justify="space-between" align={{ base: 'stretch', xs: 'flex-start' }} gap={{ base: 8, xs: 'md' }}>
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Text fw={600} fz="md" lh={1.3} lineClamp={2}>
              {r.productName}{' '}
              <Text span c="dimmed" fw={400} inherit>
                for {r.country}
              </Text>
            </Text>
            {productDetails(r) && <Text fz="xs">{productDetails(r)}</Text>}
            <Text fz="xs" c="dimmed" className="tabular">
              {r.code} · {fullDate(r.createdAt.slice(0, 10))}
            </Text>
            {version && (
              <Text fz="xs" fw={600} c="var(--mantine-color-teal-light-color)">
                {version}
              </Text>
            )}
          </Stack>
          <Flex
            direction={{ base: 'row', xs: 'column' }}
            justify={{ base: 'space-between', xs: 'flex-start' }}
            align={{ base: 'center', xs: 'flex-end' }}
            wrap="wrap"
            gap={6}
            style={{ flex: 'none' }}
          >
            <Badge color={STAGE_COLOR[r.stage]} variant="light" size="md">
              {stageLabel(r.stage)}
            </Badge>
            {r.openClarifications > 0 && (
              <Badge color="orange" variant="outline" size="md">
                Answer needed
              </Badge>
            )}
            {open ? (
              <Text fz="sm" fw={500} c={r.requiredBy ? undefined : 'dimmed'} className="tabular" style={{ whiteSpace: 'nowrap' }}>
                {expectedLabel(r.requiredBy)}
              </Text>
            ) : (
              <Text fz="xs" c="dimmed" className="tabular">
                {r.stage === 'completed' && r.completedAt
                  ? `Approved ${fullDate(r.completedAt.slice(0, 10))}`
                  : r.cancelledAt
                    ? `Cancelled ${fullDate(r.cancelledAt.slice(0, 10))}`
                    : ''}
              </Text>
            )}
          </Flex>
        </Flex>
        {open && r.progress.total > 0 && (
          <Group gap="sm" mt={10} wrap="nowrap">
            <Progress value={pct} size={5} radius="xl" color={r.stage === 'final_review' ? 'violet' : 'brand'} style={{ flex: 1 }} aria-hidden />
            <Text fz="xs" c="dimmed" className="tabular" style={{ flex: 'none' }}>
              {r.progress.ready} of {r.progress.total} documents ready
            </Text>
          </Group>
        )}
      </Box>
      <RepeatProductAction from={r.id} productName={r.productName} mt={10} />
    </Group>
  );
}
